import React, { useEffect, useMemo } from 'react'

import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

//UI
import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
} from '@chakra-ui/react'

//Hooks

import { DummyInput } from '@/components/Form'
import Input from '@/components/Form/Input'
import { VendorActionLogType } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import CostBudgetQuotationDetailTable from './CostBudgetQuotationDetailTable'
import useZZZ6 from '@/features/zzz6/useZZZ6'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { hideModal } from '@/stores/ui/modalSlice'
import { isNumber } from '@/utils/helper'
import { numberWithCommas } from '@/utils/stringHelper'
import { CostBudgetType, Currency } from '@eapproval/offer-shared'
import SingleSelect from '@/components/Form/Select/SingleSelect'
import moment from 'moment'
import { toast } from 'react-toastify'

const OfferCostBudgetQuotationDetailModal = ({
  vendorHistoryIndex,
  offerId,
  vendorIndex,
  costBudgetId,
  readOnly = false,
}) => {
  const dispatch = useDispatch()
  const { useUpdateVendorHistoryByIndex, useFetchVendorHistory } = useOfferCostBudget()
  const { useFetchAllZZZ6s } = useZZZ6()
  const updateVendorHistoryByIndex = useUpdateVendorHistoryByIndex()

  const { data: fetchedVendorHistory } = useFetchVendorHistory({
    costBudgetId,
    vendorIndex,
    historyIndex: vendorHistoryIndex,
    offerId,
    enabled: !!costBudgetId && vendorIndex !== null && vendorHistoryIndex !== null,
  })

  const historyDetail = fetchedVendorHistory?.data || fetchedVendorHistory || {}
  const detailHistory = useMemo(() => historyDetail?.history || {}, [historyDetail?.history])
  const resolvedCostBudgetType = historyDetail?.costBudgetType

  const methods = useForm({
    defaultValues: {
      ...detailHistory,
      creditTerm: detailHistory?.creditTerm,
      paymentTerm: detailHistory?.paymentTerm,
    },
  })

  const budget = useWatch({ control: methods.control, name: 'budget' })
  const discountAmount = useWatch({ control: methods.control, name: 'discountAmount' })
  const quotationDetails = useWatch({ control: methods.control, name: 'quotationDetails' })
  const submissionDate = useWatch({ control: methods.control, name: 'submissionDate' })

  useEffect(() => {
    methods.reset({
      ...detailHistory,
      creditTerm: detailHistory?.creditTerm,
      paymentTerm: detailHistory?.paymentTerm,
    })
  }, [detailHistory, methods])

  const discountRate = useMemo(() => {
    if (!isNumber(budget) || !isNumber(discountAmount)) return 0

    const originalPrice = budget + discountAmount

    if (originalPrice === 0) return 0

    return numberWithCommas((discountAmount / originalPrice) * 100)
  }, [budget, discountAmount])

  const closeModal = () => {
    dispatch(hideModal())
  }

  const handleSave = async () => {
    if (readOnly) return

    try {
      const formData = methods.getValues()
      const formattedActionType =
        detailHistory?.actionType === VendorActionLogType.VENDOR_UPLOAD
          ? 'upload'
          : detailHistory?.actionType === VendorActionLogType.VENDOR_DECLINE
            ? 'decline'
            : detailHistory?.actionType

      dispatch(showLoadingOverlay())

      await updateVendorHistoryByIndex.mutateAsync({
        costBudgetId,
        vendorIndex,
        historyIndex: vendorHistoryIndex,
        history: {
          ...formData,
          uploader: detailHistory?.uploader?._id || detailHistory?.uploader,
          actionType: formattedActionType,
        },
      })

      toast.success('Quotation detail updated successfully')
      closeModal()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update quotation detail')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={closeModal} size="lg">
      <ModalOverlay />
      <FormProvider {...methods}>
        <form>
          <ModalContent maxW={'90%'}>
            <ModalHeader>Vendor Quotation Detail</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex direction={'column'} gap="3">
                <SimpleGrid columns={4} spacing={'20px'}>
                  <SingleSelect
                    name="currency"
                    label="Currency"
                    isRequired={false}
                    isRow
                    disabled={readOnly}
                    options={Object.values(Currency).map((c) => ({ value: c, label: c }))}
                  />
                  <Input name="budget" type="number" label="Net Amount" isRequired={false} isRow disabled={readOnly} />
                  <Input
                    name="discountAmount"
                    type="number"
                    label="Discount Amount"
                    isRequired={false}
                    isRow
                    disabled={readOnly}
                  />
                  <DummyInput value={`${discountRate}%`} isReadOnly label="Discount Rate" isRow />

                  <Input name="remark" label="Remark" isRequired={false} isRow disabled={readOnly} />
                  <Input name="validDate" type="date" label="Valid Date" isRequired={false} isRow disabled={readOnly} />
                  <Input
                    name="creditTerm"
                    type="number"
                    label="Credit Terms"
                    isRequired={false}
                    isRow
                    disabled={readOnly}
                  />
                  <Input name="paymentTerm" label="Payment Terms" isRequired={false} isRow disabled={readOnly} />
                </SimpleGrid>

                <SimpleGrid columns={4} spacing={'20px'}>
                  {resolvedCostBudgetType === CostBudgetType.SUPPLY_MATERIAL_ONLY && (
                    <>
                      <Input name="warranty" label="Warranty" isRequired={false} isRow disabled={readOnly} />
                      <Input name="incoterm" label="Incoterm" isRequired={false} isRow disabled={readOnly} />
                      <Input name="leadTime" label="Leadtime (weeks)" isRequired={false} isRow disabled={readOnly} />
                    </>
                  )}
                  {resolvedCostBudgetType === CostBudgetType.SUPPLY_SITE_WORK ||
                    (resolvedCostBudgetType === CostBudgetType.SUPPLY_SITE_WORK_RFQ && (
                      <>
                        <Input name="insurance" label="Insurance" isRequired={false} isRow disabled={readOnly} />
                        <Input name="incoterm" label="Incoterm" isRequired={false} isRow disabled={readOnly} />
                        <Input name="DLP" label="DLP" isRequired={false} isRow disabled={readOnly} />
                        <Input name="leadTime" label="Leadtime (weeks)" isRequired={false} isRow disabled={readOnly} />
                      </>
                    ))}

                  {(resolvedCostBudgetType === CostBudgetType.SUPPLY_ENGINEERING_SERVICE ||
                    resolvedCostBudgetType === CostBudgetType.SUPPLY_MAINTENANCE_SERVICE) && (
                    <Input name="insurance" label="Insurance" isRequired={false} isRow disabled={readOnly} />
                  )}
                </SimpleGrid>

                <CostBudgetQuotationDetailTable
                  costBudgetType={resolvedCostBudgetType}
                  data={quotationDetails ?? []}
                  disabled={readOnly}
                  excelAdditionalData={{
                    'Vendor Name': historyDetail?.vendorName || '(Missing)',
                    'Vendor User': historyDetail?.vendorUser || '(Missing)',
                    'Submission Time': moment(submissionDate).format('DD-MMM-YYYY  HH:mm:ss'),
                    Subject: historyDetail?.projectName || '(Missing)',
                    'Cost Budget': historyDetail?.costBudgetScopeOfWork || '(Missing)',
                    'PM Name': historyDetail?.managerName || '(Missing)',
                    Version: (vendorHistoryIndex ?? 0) + 1,
                  }}
                />
              </Flex>
            </ModalBody>
            <ModalFooter>
              <HStack>
                {!readOnly && (
                  <Button colorScheme="green" onClick={handleSave}>
                    Save
                  </Button>
                )}
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default OfferCostBudgetQuotationDetailModal
